import {
  type IntranetHighlightSpan,
  type PinIntranetContentRequest,
} from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '@/lib/format';
import { cn } from '@/lib/utils';

export const INTRANET_HIGHLIGHT_SPAN_OPTIONS: {
  value: IntranetHighlightSpan;
  label: string;
}[] = [
  { value: 'today', label: 'Heute noch' },
  { value: 'd3', label: '3 Tage' },
  { value: 'd7', label: '7 Tage' },
  { value: 'd14', label: '14 Tage' },
  { value: 'custom', label: 'Eigener Zeitpunkt' },
];

export function IntranetHighlightDialog({
  open,
  title,
  description,
  initialSpan,
  initialUntil,
  pending,
  onCancel,
  onSave,
}: {
  open: boolean;
  title: string;
  description?: string;
  initialSpan: IntranetHighlightSpan | null;
  initialUntil: string | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (input: PinIntranetContentRequest) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) {
          onCancel();
        }
      }}
    >
      {open ? (
        <HighlightDialogFields
          title={title}
          description={description}
          initialSpan={initialSpan}
          initialUntil={initialUntil}
          pending={pending}
          onCancel={onCancel}
          onSave={onSave}
        />
      ) : null}
    </Dialog>
  );
}

function HighlightDialogFields({
  title,
  description,
  initialSpan,
  initialUntil,
  pending,
  onCancel,
  onSave,
}: {
  title: string;
  description?: string;
  initialSpan: IntranetHighlightSpan | null;
  initialUntil: string | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (input: PinIntranetContentRequest) => void;
}) {
  const [span, setSpan] = useState<IntranetHighlightSpan>(initialSpan ?? 'today');
  const [until, setUntil] = useState(toDatetimeLocalValue(initialUntil));

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>
      <div className="flex flex-wrap gap-2">
        {INTRANET_HIGHLIGHT_SPAN_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={span === option.value}
            className={cn(
              'rounded-full border px-3 py-1 text-sm',
              span === option.value
                ? 'border-brand-navy bg-brand-navy text-white'
                : 'border-border',
            )}
            onClick={() => setSpan(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {span === 'custom' ? (
        <div className="space-y-2">
          <Label htmlFor="highlight-until">Eigener Zeitpunkt</Label>
          <Input
            id="highlight-until"
            type="datetime-local"
            value={until}
            onChange={(event) => setUntil(event.target.value)}
          />
        </div>
      ) : initialUntil && initialSpan === span ? (
        <p className="text-sm text-muted-foreground">Bis {formatUntil(initialUntil)}</p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={pending || (span === 'custom' && !fromDatetimeLocalValue(until))}
          onClick={() => {
            if (span === 'custom') {
              const iso = fromDatetimeLocalValue(until);
              if (!iso) {
                return;
              }
              onSave({ span, until: iso });
              return;
            }
            onSave({ span });
          }}
        >
          Speichern
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function formatUntil(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}
